import { useRef, useEffect, useState } from 'react';
import * as bodyPix from '@tensorflow-models/body-pix';
import '@tensorflow/tfjs';

const CameraBlur = ({
  width = 640,
  height = 480,
  background = 'blur', // 'blur', 'black', '#color', or image URL
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [bgImage, setBgImage] = useState(null);

  useEffect(() => {
    let stream;
    let isMounted = true;

    const waitForVideoReady = () =>
      new Promise(resolve => {
        const checkReady = () => {
          if (
            videoRef.current &&
            videoRef.current.videoWidth > 0 &&
            videoRef.current.videoHeight > 0
          ) {
            resolve();
          } else {
            requestAnimationFrame(checkReady);
          }
        };
        checkReady();
      });

    const loadCamera = async () => {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { width, height },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        await new Promise(resolve => {
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play();
            resolve();
          };
        });

        await waitForVideoReady();
      }
    };

    const runBodyPix = async () => {
      const net = await bodyPix.load();

     const drawFrame = async () => {
  if (!isMounted || !videoRef.current || !canvasRef.current) return;

  const video = videoRef.current;
  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    requestAnimationFrame(drawFrame);
    return;
  }

  const segmentation = await net.segmentPerson(video, {
    internalResolution: 'medium',
    segmentationThreshold: 0.7,
  });

  ctx.clearRect(0, 0, width, height);

  if (background === 'blur') {
    bodyPix.drawBokehEffect(canvas, video, segmentation, 6, 3, false);
  } else {
    // 1. Draw background (solid color or image)
    if (
      background.startsWith('#') ||
      background === 'black' ||
      background === 'green' ||
      /^[a-zA-Z]+$/.test(background) // allow named colors
    ) {
      ctx.fillStyle = background === 'black' ? '#000' : background;
      ctx.fillRect(0, 0, width, height);
    } else if (bgImage) {
      ctx.drawImage(bgImage, 0, 0, width, height);
    }

    // 2. Draw the person on top
    const mask = bodyPix.toMask(segmentation, {r: 0, g: 0, b: 0, a: 0}, {r: 0, g: 0, b: 0, a: 255});
    bodyPix.drawMask(canvas, video, mask, 1, 0, false);
  }

  requestAnimationFrame(drawFrame);
};


      drawFrame();
    };

    loadCamera();
    runBodyPix();

    return () => {
      isMounted = false;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [background, width, height, bgImage]);

  // Load background image if it's a URL
  useEffect(() => {
    if (
      background.startsWith('http://') ||
      background.startsWith('https://')
    ) {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Required for cross-origin images
      img.src = background;
      img.onload = () => setBgImage(img);
    } else {
      setBgImage(null);
    }
  }, [background]);

  return (
    <div>
      <video
        ref={videoRef}
        width={width}
        height={height}
        style={{ display: 'none' }}
      />
      <canvas ref={canvasRef} width={width} height={height} />
    </div>
  );
};

export default CameraBlur;
